// 건별수익률현황 화면
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { fetchProfitStatus, fetchProfitStatusSummary } from '../../api/profit';
import { fetchRealtimePrices, fetchBenchmarkPrices } from '../../api/price';
import { fetchAccounts } from '../../api/accounts';
import PageLayout from '../../components/PageLayout';
import MultiSelectCombo from '../../components/common/MultiSelectCombo';
import StockChartPopup from '../../components/common/StockChartPopup';
import AlertButton from '../../components/common/AlertButton';
import AlertQuickPopup from '../../components/common/AlertQuickPopup';
import StockCompareModal from '../../components/common/StockCompareModal';
import { handleTickerClick, handleExchangeRateClick, handleGoldClick, handleKospiClick, handleKosdaqClick, handleSp500Click, handleNasdaq100Click } from '../../utils/naverChart';
import { goToBuyRecord, goToSellRecord } from '../../utils/tabNav';
import './ProfitStatusPage.css';

const PENDING_COLOR = '#e67e22';

const currentYear = new Date().getFullYear();
const yearOptions = ['전체', ...Array.from({ length: 5 }, (_, i) => String(currentYear - i))];

// ── 컬럼 정의 ──────────────────────────────────────────────────
// pendingSelf     : 자기 셀이 null이면 오렌지
// pendingOn       : 지정 필드 null이면 오렌지
// pendingSoldSelf : is_sold=TRUE이고 자기 셀 null이면 오렌지
// pendingSoldOn   : is_sold=TRUE이고 지정 필드 null이면 오렌지
// color: true → 양수=빨강, 음수=파랑
// subtotal: 'sum' | 'avg' | null
// sortable: 헤더 클릭 정렬
// 컬럼 폭: 매수환율/매도환율 추가(전체/종목별 탭)로 가로폭이 늘어나 전반적으로 축소 조정함 (2026-09)
const BASE_COLUMNS = [
  { id: 'account_name',          label: '계좌',       w: 74,  align: 'left',   fmt: null,   sortable: true,  resizable: true  },
  { id: 'term_type',             label: '장단기',     w: 60,  align: 'center', fmt: null,   sortable: true                    },
  { id: 'stock_name',            label: '종목명',     w: 150, align: 'left',   fmt: null,   sortable: true,  resizable: true  },
  { id: 'ticker',                label: '티커',       w: 60,  align: 'center', fmt: null,   sortable: true                    },
  { id: 'buy_date',              label: '매수일',     w: 80,  align: 'center', fmt: null,   sortable: true,  pendingSelf: true },
  { id: 'turn_no',               label: '차수',       w: 40,  align: 'center', fmt: 'turn'                                    },
  { id: 'buy_price',             label: '매수단가',   w: 82,  align: 'right',  fmt: 'num',                   pendingOn: 'buy_date'     },
  { id: 'quantity',              label: '수량',       w: 60,  align: 'right',  fmt: 'qty',   subtotal: 'sum', pendingOn: 'buy_date'    },
  { id: 'buy_amount',            label: '매수금액',   w: 94,  align: 'right',  fmt: 'num',   subtotal: 'sum', pendingOn: 'buy_date'    },
  { id: 'target_profit_rate',    label: '목표수익률', w: 68,  align: 'center', fmt: 'rate'                                            },
  { id: 'current_price',         label: '현재가',     w: 82,  align: 'right',  fmt: 'num'                                             },
  { id: 'diff_change',           label: '당일등락',   w: 104, align: 'right',  fmt: 'diff'                                             },
  { id: 'current_profit_rate',   label: '현재수익률', w: 68,  align: 'center', fmt: 'rate',  color: true,    subtotal: 'avg'           },
  { id: 'unrealized_profit',     label: '예상손익',   w: 94,  align: 'right',  fmt: 'num',   color: true,    subtotal: 'sum'           },
  { id: 'sell_date',             label: '매도일',     w: 80,  align: 'center', fmt: null,   sortable: true,  pendingSoldSelf: true     },
  { id: 'sell_price',            label: '매도단가',   w: 82,  align: 'right',  fmt: 'num',                   pendingSoldOn: 'sell_date'},
  { id: 'sell_amount',           label: '매도금액',   w: 94,  align: 'right',  fmt: 'num',   subtotal: 'sum'                           },
  { id: 'confirmed_profit',      label: '확정손익',   w: 94,  align: 'right',  fmt: 'num',   color: true,    subtotal: 'sum'           },
  { id: 'confirmed_profit_rate', label: '확정수익률', w: 68,  align: 'center', fmt: 'rate',  color: true,    subtotal: 'avg'           },
  { id: 'after_sell_change_rate',label: '매도후증감', w: 68,  align: 'center', fmt: 'rate',  color: true                               },
];

const SECTOR_COL = { id: 'sector_name', label: '섹터', w: 80, align: 'center', fmt: null, sortable: true };
// '전체'/'종목별' 탭에서 target_profit_rate 대신 표시 — 매수건별 메모(tb_buy_history.memo)
const MEMO_COL   = { id: 'memo', label: '메모', w: 140, align: 'left', fmt: null };
const MEMO_TABS  = new Set(['all', 'stock']);

// '전체'/'종목별' 탭 전용 — 매수환율/매도환율 (컬럼 수가 많아 폭은 좁게)
const BUY_RATE_COL  = { id: 'buy_exchange_rate',  label: '매수환율', w: 64, align: 'right', fmt: 'num' };
const SELL_RATE_COL = { id: 'sell_exchange_rate', label: '매도환율', w: 64, align: 'right', fmt: 'num' };

// 탭에 따라 2번째 컬럼(term_type ↔ sector_name), '전체'/'종목별' 탭은 목표수익률 → 메모 교체
// + '전체'/'종목별' 탭에는 매수금액/매도금액 뒤에 매수환율/매도환율 컬럼 삽입
const getColumns = (tab, acctW, nameW) => {
  const cols = BASE_COLUMNS.map(col => {
    if (col.id === 'term_type' && tab === 'sector') return SECTOR_COL;
    if (col.id === 'target_profit_rate' && MEMO_TABS.has(tab)) return MEMO_COL;
    if (col.id === 'account_name') return { ...col, w: acctW };
    if (col.id === 'stock_name')   return { ...col, w: nameW };
    return col;
  });
  if (!MEMO_TABS.has(tab)) return cols;
  const result = [];
  cols.forEach(col => {
    result.push(col);
    if (col.id === 'buy_amount')  result.push(BUY_RATE_COL);
    if (col.id === 'sell_amount') result.push(SELL_RATE_COL);
  });
  return result;
};

// 소계 라벨이 차지하는 앞쪽 컬럼 수 (buy_price부터 숫자 표시)
const LABEL_COLSPAN = 6;


// ── 포맷 헬퍼 ──────────────────────────────────────────────────
const fNum  = v => v != null && v !== '' ? Number(v).toLocaleString('ko-KR') : '';
const fQty  = v => v != null && v !== ''
  ? Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 }) : '';
const fRate = v => v != null && v !== '' ? `${Number(v).toFixed(2)}%` : '';
const fMoney = v => v == null ? '' : Math.round(Number(v)).toLocaleString('ko-KR');

const sign   = v => v > 0 ? '+' : '';
const fDiff  = (prevDiff, diffRate) => {
  if (prevDiff == null && diffRate == null) return '';
  const d = prevDiff != null ? `${sign(prevDiff)}${Math.round(prevDiff).toLocaleString('ko-KR')}` : '';
  const r = diffRate  != null ? `${sign(diffRate)}${Number(diffRate).toFixed(2)}%` : '';
  if (d && r) return `${d} (${r})`;
  return d || r;
};
const diffColor = v => v == null ? undefined : v > 0 ? '#d32f2f' : v < 0 ? '#1565c0' : undefined;

// ── 지표 비교 영역 포맷 헬퍼 ──────────────────────────────────
// 정수면 그대로, 소수가 있으면(해외종목/환율 등) 소수 2자리로 표시
const fBenchPrice = v => {
  if (v == null) return '-';
  const n = Number(v);
  return Number.isInteger(n)
    ? n.toLocaleString('ko-KR')
    : n.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// 전일대비 증감액 + 등락률을 "+26,000, +0.2%" 형태 한 문자열로 결합
const fBenchDiffText = (diffAmt, rate) => {
  if (diffAmt == null && rate == null) return null;
  const d = diffAmt != null ? `${sign(diffAmt)}${Math.round(diffAmt).toLocaleString('ko-KR')}` : null;
  const r = rate     != null ? `${sign(rate)}${Number(rate).toFixed(1)}%` : null;
  return [d, r].filter(Boolean).join(', ');
};

// exchangeRate(kospi, kosdaq, sp500, nasdaq100, usd_krw, gold) + benchmark.stocks 순서로
// 하나의 칩 배열을 구성. 렌더링 시 칩 사이사이에 구분자("|")를 끼워 넣기 위해 미리 평탄화해둔다.
// 표시 순서: 코스피 → 코스닥 → S&P500 → 나스닥100 → 환율(USD/KRW) → 금 → 등록된 지표종목들
const buildBenchmarkChips = (benchmark) => {
  const chips = [];

  const er = benchmark.exchangeRate;
  if (er?.kospi != null) {
    const diffText = fBenchDiffText(er.kospi_diff, er.kospi_rate);
    chips.push(
      <span
        key="idx-kospi"
        className="benchmark-chip benchmark-chip-fx"
        onClick={handleKospiClick}
        title="네이버 증권 코스피 지수 보기"
      >
        <span className="benchmark-chip-name">코스피</span>
        <span className="benchmark-chip-price">{fBenchPrice(er.kospi)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(er.kospi_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  }
  if (er?.kosdaq != null) {
    const diffText = fBenchDiffText(er.kosdaq_diff, er.kosdaq_rate);
    chips.push(
      <span
        key="idx-kosdaq"
        className="benchmark-chip benchmark-chip-fx"
        onClick={handleKosdaqClick}
        title="네이버 증권 코스닥 지수 보기"
      >
        <span className="benchmark-chip-name">코스닥</span>
        <span className="benchmark-chip-price">{fBenchPrice(er.kosdaq)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(er.kosdaq_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  }
  if (er?.sp500 != null) {
    const diffText = fBenchDiffText(er.sp500_diff, er.sp500_rate);
    chips.push(
      <span
        key="idx-sp500"
        className="benchmark-chip benchmark-chip-fx"
        onClick={handleSp500Click}
        title="네이버 증권 S&P500 지수 보기"
      >
        <span className="benchmark-chip-name">S&P500</span>
        <span className="benchmark-chip-price">{fBenchPrice(er.sp500)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(er.sp500_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  }
  if (er?.nasdaq100 != null) {
    const diffText = fBenchDiffText(er.nasdaq100_diff, er.nasdaq100_rate);
    chips.push(
      <span
        key="idx-nasdaq100"
        className="benchmark-chip benchmark-chip-fx"
        onClick={handleNasdaq100Click}
        title="네이버 증권 나스닥100 지수 보기"
      >
        <span className="benchmark-chip-name">나스닥100</span>
        <span className="benchmark-chip-price">{fBenchPrice(er.nasdaq100)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(er.nasdaq100_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  }
  if (er?.usd_krw != null) {
    const diffText = fBenchDiffText(er.usd_krw_diff, er.usd_krw_rate);
    chips.push(
      <span
        key="fx-usd"
        className="benchmark-chip benchmark-chip-fx"
        onClick={handleExchangeRateClick}
        title="네이버 증권 원달러 환율 보기"
      >
        <span className="benchmark-chip-name">USD/KRW</span>
        <span className="benchmark-chip-price">{fBenchPrice(er.usd_krw)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(er.usd_krw_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  }
  if (er?.gold != null) {
    const diffText = fBenchDiffText(er.gold_diff, er.gold_rate);
    chips.push(
      <span
        key="fx-gold"
        className="benchmark-chip benchmark-chip-fx"
        onClick={handleGoldClick}
        title="네이버 증권 국제 금 시세 보기"
      >
        <span className="benchmark-chip-name">금</span>
        <span className="benchmark-chip-price">{fBenchPrice(er.gold)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(er.gold_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  }

  benchmark.stocks.forEach(s => {
    const diffText = fBenchDiffText(s.prev_diff, s.diff_rate);
    chips.push(
      <span
        key={`s-${s.ticker}`}
        className="benchmark-chip"
        onClick={() => handleTickerClick(s.ticker, s.market_type)}
        title="네이버 증권 차트 보기"
      >
        <span className="benchmark-chip-name">{s.stock_name || s.ticker}</span>
        <span className="benchmark-chip-price">{fBenchPrice(s.current_price)}</span>
        {diffText && (
          <span className="benchmark-chip-rate" style={{ color: diffColor(s.diff_rate) }}>
            ({diffText})
          </span>
        )}
      </span>
    );
  });

  return chips;
};

const formatCell = (col, value) => {
  if (value == null || value === '') return '';
  switch (col.fmt) {
    case 'num':  return fNum(value);
    case 'rate': return fRate(value);
    case 'qty':  return fQty(value);
    case 'turn': return `${value}차`;
    default:     return String(value);
  }
};

// 양수=빨강, 음수=파랑
const profitColor = v => {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  if (n > 0) return '#c0392b';
  if (n < 0) return '#1a6eb5';
  return undefined;
};

// ── 소계 계산 ──────────────────────────────────────────────────
const calcSummary = items => {
  const result = {};
  BASE_COLUMNS.filter(c => c.subtotal).forEach(c => {
    const vals = items.map(i => i[c.id]).filter(v => v != null);
    if (!vals.length) { result[c.id] = null; return; }
    const total = vals.reduce((s, v) => s + Number(v), 0);
    result[c.id] = c.subtotal === 'sum'
      ? total
      : Math.round((total / vals.length) * 100) / 100;
  });
  // 수량 소계는 정수로 표시 (분할매수 등으로 인한 소수점 오차 제거)
  if (result.quantity != null) result.quantity = Math.round(result.quantity);
  // 매수단가 소계 = 가중평균 (매수금액 합계 ÷ 수량 합계), 정수로 표시
  result.buy_price = result.quantity ? Math.round(result.buy_amount / result.quantity) : null;

  // 수익률 = 손익 합계 ÷ 매수금액 합계 × 100 (단순평균 대신 가중평균)
  const buyAmt = result.buy_amount;
  if (buyAmt) {
    const unrlz = result.unrealized_profit;
    const conf  = result.confirmed_profit;
    result.current_profit_rate   = unrlz != null ? Math.round(unrlz / buyAmt * 10000) / 100 : null;
    result.confirmed_profit_rate = conf  != null ? Math.round(conf  / buyAmt * 10000) / 100 : null;
  } else {
    result.current_profit_rate   = null;
    result.confirmed_profit_rate = null;
  }
  return result;
};

// ── 그룹핑 ────────────────────────────────────────────────────
const groupByKey = (data, fn) => {
  const map = {}, order = [];
  data.forEach(item => {
    const key = fn(item) || '(없음)';
    if (!map[key]) { map[key] = []; order.push(key); }
    map[key].push(item);
  });
  return order.map(k => ({ key: k, items: map[k] }));
};

// 종목(ticker) 그룹핑 → sort_order asc → 그룹 내 계좌명 asc → 매수일자 asc → 차수 asc
// (그룹 라벨은 "종목명 (티커)", 그룹핑 키는 ticker로 분리 — 동명 종목 충돌 방지)
const buildStockGroups = data => {
  const groups = {}, order = [];
  data.forEach(item => {
    const key = item.ticker;
    if (!groups[key]) {
      groups[key] = {
        stock_name: item.stock_name || item.ticker,
        ticker:     item.ticker,
        sort_order: item.stock_sort_order != null ? item.stock_sort_order : 9999,
        items:      [],
      };
      order.push(key);
    }
    groups[key].items.push(item);
  });
  order.forEach(key => {
    groups[key].items.sort((a, b) => {
      const an = a.account_name || '', bn = b.account_name || '';
      if (an !== bn) return an.localeCompare(bn, 'ko');
      const ad = a.buy_date || '', bd = b.buy_date || '';
      if (ad !== bd) return ad.localeCompare(bd);
      return (a.turn_no || 0) - (b.turn_no || 0);
    });
  });
  return order
    .map(key => groups[key])
    .sort((a, b) => a.sort_order !== b.sort_order
      ? a.sort_order - b.sort_order
      : String(a.stock_name).localeCompare(String(b.stock_name), 'ko'));
};

const buildRenderRows = (data, tab) => {
  if (!data.length) return [];
  const rows = [];
  const addGroup = (label, items) => {
    rows.push({ type: 'header', label, count: items.length });
    items.forEach(item => rows.push({ type: 'data', item }));
    rows.push({ type: 'subtotal', label, summary: calcSummary(items) });
  };
  if (tab === 'all') {
    data.forEach(item => rows.push({ type: 'data', item }));
  } else if (tab === 'stock') {
    buildStockGroups(data).forEach(g => addGroup(`${g.stock_name} (${g.ticker})`, g.items));
  } else {
    const groupFns = {
      account: i => i.account_name,
      sector:  i => i.sector_name,
      term:    i => i.term_type,
    };
    groupByKey(data, groupFns[tab]).forEach(({ key, items }) => addGroup(key, items));
  }
  rows.push({ type: 'total', summary: calcSummary(data), count: data.length });
  return rows;
};

// ── 메인 컴포넌트 ──────────────────────────────────────────────
const ProfitStatusPage = () => {
  const [filterAccount, setFilterAccount] = useState([]);
  const [inclUnsold,    setInclUnsold]    = useState(true);
  const [inclSold,      setInclSold]      = useState(false);
  const [filterYear,    setFilterYear]    = useState(String(currentYear));
  const [inclPending,     setInclPending]     = useState(true);
  const [inclSellPending, setInclSellPending] = useState(true);
  const [autoRefresh,     setAutoRefresh]     = useState(false);
  const [isRefreshing,    setIsRefreshing]    = useState(false);

  const [activeTab,    setActiveTab]    = useState('account');
  const [rawData,      setRawData]      = useState([]);
  const [accounts,     setAccounts]     = useState([]);
  const [summary,      setSummary]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSource,  setPriceSource]  = useState(null);
  const [message,      setMessage]      = useState({ text: '', type: '' });
  const [chartPopup,   setChartPopup]   = useState(null);
  const [alertPopup,   setAlertPopup]   = useState(null);
  const [compareModal, setCompareModal] = useState(null);

  // ── 지표 비교 영역 (보유/매수와 무관한 순수 시세 워치리스트, 항상 펼침) ──
  const [benchmark, setBenchmark] = useState(null);   // null = 미로딩/실패/데이터없음

  // 클라이언트 정렬
  const [sortField,     setSortField]     = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // 컬럼 너비 (리사이즈 가능)
  const [acctW, setAcctW] = useState(74);
  const [nameW, setNameW] = useState(150);
  const resizingRef = useRef(null);

  // 자동갱신에서 rawData 최신값 참조용
  const rawDataRef = useRef([]);

  useEffect(() => {
    fetchAccounts().then(setAccounts).catch(() => {});
  }, []);

  // 지표 비교 데이터 로드 — 다른 조회조건과 무관하게 1회 로드.
  // 실패하거나 표시할 데이터가 없으면 benchmark를 null로 유지해 영역 자체를 숨긴다.
  useEffect(() => {
    fetchBenchmarkPrices()
      .then(res => {
        const stocks = res?.stocks || [];
        const er     = res?.exchange_rate;
        const hasFx  = !!er && (er.kospi != null || er.kosdaq != null || er.sp500 != null || er.nasdaq100 != null || er.usd_krw != null || er.gold != null);
        if (!stocks.length && !hasFx) { setBenchmark(null); return; }
        setBenchmark({ stocks, exchangeRate: hasFx ? er : null });
      })
      .catch(() => setBenchmark(null));
  }, []);

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // ── 실시간 시세 적용 ───────────────────────────────────────
  const applyRealtimePrices = (items, priceMap) =>
    items.map(item => {
      const p = priceMap[item.ticker];
      if (!p || p.current_price == null) return item;
      const curr = p.current_price;
      if (item.is_sold) {
        const after = item.sell_price && item.sell_price > 0
          ? Math.round((curr - item.sell_price) / item.sell_price * 10000) / 100
          : null;
        return { ...item, current_price: curr, after_sell_change_rate: after };
      }
      const rate  = item.buy_price ? Math.round((curr - item.buy_price) / item.buy_price * 10000) / 100 : null;
      const unrlz = item.buy_price && item.quantity ? Math.round((curr - item.buy_price) * item.quantity) : null;
      return { ...item, current_price: curr, diff_rate: p.diff_rate, current_profit_rate: rate, unrealized_profit: unrlz };
    });

  // ── 실시간 시세 갱신 (그리드 업데이트 + unsold_profit_loss 클라이언트 재계산) ──
  const runRealtimeUpdate = useCallback(async (currentData) => {
    const tickers = [...new Set(currentData.map(i => i.ticker))];
    if (!tickers.length) return;
    setPriceLoading(true);
    try {
      const res = await fetchRealtimePrices(tickers);
      const priceMap = {};
      for (const p of res.data) priceMap[p.ticker] = p;
      const updated = applyRealtimePrices(currentData, priceMap);
      rawDataRef.current = updated;
      setRawData(updated);
      setPriceSource(res.data.some(p => p.source === 'cached') ? 'mixed' : 'realtime');
      const unsoldProfit = updated
        .filter(i => !i.is_sold && i.buy_date)
        .reduce((s, i) => s + (i.unrealized_profit ?? 0), 0);
      setSummary(prev => prev ? { ...prev, unsold_profit_loss: Math.round(unsoldProfit) } : prev);
    } catch {
      setPriceSource(null);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // ── 데이터 조회 ────────────────────────────────────────────
  const fetchData = useCallback(async (resetSort = false) => {
    if (!inclUnsold && !inclSold) {
      setRawData([]);
      setSummary(null);
      showMessage('미매도 또는 매도완료 중 하나 이상을 선택해주세요.', 'error');
      return;
    }
    if (resetSort) { setSortField(null); setSortDirection('asc'); }
    setLoading(true);
    setPriceSource(null);
    try {
      const params = { is_sold_n: inclUnsold, is_sold_y: inclSold, include_sell_plan: inclSellPending, include_buy_plan: inclPending };
      if (filterAccount.length) params.account_name = filterAccount.join(',');
      if (filterYear && filterYear !== '전체') params.year = Number(filterYear);

      const summaryParams = { include_sell_plan: inclSellPending };
      if (filterAccount.length) summaryParams.accounts = filterAccount.join(',');
      if (filterYear && filterYear !== '전체') summaryParams.year = Number(filterYear);

      const [result, sumResult] = await Promise.all([
        fetchProfitStatus(params),
        fetchProfitStatusSummary(summaryParams),
      ]);

      rawDataRef.current = result;
      setRawData(result);
      setSummary(sumResult);
      showMessage(`${result.length}건 조회되었습니다.`);

      runRealtimeUpdate(result);
    } catch {
      showMessage('조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [inclUnsold, inclSold, inclPending, filterAccount, filterYear, inclSellPending, runRealtimeUpdate]);

  // 조건 변경 시 자동 재조회 (마운트 포함)
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // ── 자동갱신 (3분 interval) ────────────────────────────────
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (rawDataRef.current.length > 0) {
        runRealtimeUpdate(rawDataRef.current);
      }
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, runRealtimeUpdate]);

  // ── 정렬 핸들러 ────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortArrow = (colId) => {
    if (sortField !== colId) return null;
    return <span className="sort-arrow">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
  };

  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing || priceLoading || !rawDataRef.current.length) return;
    setIsRefreshing(true);
    try {
      await runRealtimeUpdate(rawDataRef.current);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, priceLoading, runRealtimeUpdate]);

  // ── displayData: 클라이언트 정렬 ────────
  const displayData = useMemo(() => {
    let d = rawData;
    if (!sortField) return d;
    return [...d].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return sortDirection === 'asc' ? 1 : -1;
      if (bv == null) return sortDirection === 'asc' ? -1 : 1;
      const cmp = typeof av === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'ko');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [rawData, sortField, sortDirection]);

  const renderRows = useMemo(() => buildRenderRows(displayData, activeTab), [displayData, activeTab]);

  // ── 컬럼 너비 리사이즈 ─────────────────────────────────────
  const startResize = (e, colId) => {
    e.stopPropagation();
    e.preventDefault();
    const startW = colId === 'account_name' ? acctW : nameW;
    resizingRef.current = { colId, startX: e.clientX, startW };

    const onMove = ev => {
      if (!resizingRef.current) return;
      const { colId: cid, startX, startW: sw } = resizingRef.current;
      const minW = cid === 'account_name' ? 70 : 100;
      const newW = Math.max(minW, sw + (ev.clientX - startX));
      if (cid === 'account_name') setAcctW(newW);
      else setNameW(newW);
    };
    const onUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── 합계 색상 ──────────────────────────────────────────────
  const sumColor = v => {
    if (v == null) return undefined;
    if (v > 0) return '#c0392b';
    if (v < 0) return '#1a6eb5';
    return undefined;
  };

  // ── 툴바 ──────────────────────────────────────────────────
  const toolbar = (
    <div className="profit-filter">
      <MultiSelectCombo
        options={accounts.map(a => ({ value: a.account_name, label: a.account_name }))}
        value={filterAccount}
        onChange={setFilterAccount}
        placeholder="전체"
      />

      <label className="profit-chk">
        <input type="checkbox" checked={inclUnsold} onChange={e => setInclUnsold(e.target.checked)} />
        미매도
      </label>
      <label className="profit-chk">
        <input type="checkbox" checked={inclSold} onChange={e => setInclSold(e.target.checked)} />
        매도완료
      </label>

      <select
        className="profit-select profit-year-select"
        value={filterYear}
        onChange={e => setFilterYear(e.target.value)}
      >
        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
      </select>

      <button className="btn btn-primary" onClick={() => fetchData(true)} disabled={loading}>
        {loading ? '조회 중...' : '조회(재정렬)'}
      </button>

      <label className="profit-chk">
        <input type="checkbox" checked={inclPending} onChange={e => setInclPending(e.target.checked)} />
        매수진행포함
      </label>
      <label className="profit-chk">
        <input type="checkbox" checked={inclSellPending} onChange={e => setInclSellPending(e.target.checked)} />
        매도진행포함
      </label>
      {summary && (
        <>
          <span className="profit-sep" />
          <span className="profit-summary-item">
            미매도 매수금액:&nbsp;<strong>{fMoney(summary.unsold_buy_amount)}</strong>
          </span>
          <span className="profit-sep" />
          <span className="profit-summary-item">
            미매도 예상손익:&nbsp;
            <strong style={{ color: sumColor(summary.unsold_profit_loss) }}>
              {fMoney(summary.unsold_profit_loss)}
            </strong>
          </span>
          <span className="profit-sep" />
          <span className="profit-summary-item">
            매도확정손익:&nbsp;
            <strong style={{ color: sumColor(summary.confirmed_profit_loss) }}>
              {fMoney(summary.confirmed_profit_loss)}
            </strong>
          </span>
        </>
      )}
    </div>
  );

  // ── 데이터행 렌더 ──────────────────────────────────────────
  const columns = useMemo(() => getColumns(activeTab, acctW, nameW), [activeTab, acctW, nameW]);

  // ── 엑셀 다운로드 (현재 화면에 표시된 그리드 그대로 — 모든 탭 동일 로직) ─────────
  const handleExcelDownload = () => {
    if (!renderRows.length) {
      showMessage('다운로드할 데이터가 없습니다.', 'error');
      return;
    }

    const NUMERIC_FMTS = ['num', 'qty', 'rate', 'turn'];
    const aoa = [columns.map(c => c.label)];
    const merges = [];

    renderRows.forEach(row => {
      const rIdx = aoa.length;
      if (row.type === 'header') {
        const arr = new Array(columns.length).fill('');
        arr[0] = `${row.label} (${row.count}건)`;
        aoa.push(arr);
        merges.push({ s: { r: rIdx, c: 0 }, e: { r: rIdx, c: columns.length - 1 } });
      } else if (row.type === 'data') {
        const item = row.item;
        aoa.push(columns.map(col => {
          if (col.id === 'diff_change') return !item.is_sold ? fDiff(item.prev_diff, item.diff_rate) : '';
          const v = item[col.id];
          if (v == null || v === '') return '';
          return NUMERIC_FMTS.includes(col.fmt) ? Number(v) : v;
        }));
      } else {
        const label = row.type === 'total' ? `전체 합계 (${row.count}건)` : `${row.label} 소계`;
        const arr = new Array(columns.length).fill('');
        arr[0] = label;
        columns.slice(LABEL_COLSPAN).forEach((col, i) => {
          const val = row.summary?.[col.id];
          arr[LABEL_COLSPAN + i] = val != null ? Number(val) : '';
        });
        aoa.push(arr);
        merges.push({ s: { r: rIdx, c: 0 }, e: { r: rIdx, c: LABEL_COLSPAN - 1 } });
      }
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;
    ws['!cols'] = columns.map(c => ({ wch: Math.max(8, Math.round(c.w / 7)) }));

    const wb = XLSX.utils.book_new();
    const tabLabel = { account: '계좌별', sector: '섹터별', term: '장단기별', stock: '종목별', all: '전체' }[activeTab];
    XLSX.utils.book_append_sheet(wb, ws, `건별수익률현황_${tabLabel}`);

    const today = new Date();
    const dateStr = today.getFullYear()
      + String(today.getMonth() + 1).padStart(2, '0')
      + String(today.getDate()).padStart(2, '0');
    const fileName = activeTab === 'stock'
      ? `건별수익률현황_종목별_${dateStr}.xlsx`
      : `건별수익률현황_${dateStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const renderDataRow = (item, idx) => (
    <tr key={`d-${item.buy_id}-${idx}`}
        className={item.is_sold ? 'row-sold' : 'row-unsold'}>
      {columns.map(col => {
        let cellColor;
        if (col.id === 'diff_change')                                   cellColor = !item.is_sold ? diffColor(item.diff_rate) : undefined;
        else if (col.pendingSelf && !item[col.id])                      cellColor = PENDING_COLOR;
        else if (col.pendingSoldSelf && item.is_sold && !item[col.id])  cellColor = PENDING_COLOR;
        else if (col.pendingOn    && !item[col.pendingOn])              cellColor = PENDING_COLOR;
        else if (col.pendingSoldOn && item.is_sold && !item[col.pendingSoldOn]) cellColor = PENDING_COLOR;
        else if (col.color)                                             cellColor = profitColor(item[col.id]);

        return (
          <td key={col.id}
              style={{ width: col.w, minWidth: col.w, textAlign: col.align, color: cellColor }}>
            {col.id === 'stock_name' ? (
              <>
                <span
                  className="stock-chart-link"
                  onClick={() => setChartPopup({
                    ticker: item.ticker,
                    stockName: item.stock_name || item.ticker,
                    accountName: item.account_name,
                    termType: item.term_type || null,
                    currentPrice: item.current_price || null,
                    diffRate: item.diff_rate || null,
                    turns: item.buy_price > 0 ? [{ turn_no: 1, buy_date: item.buy_date || null, buy_price: item.buy_price, quantity: item.quantity, profit_rate: item.current_profit_rate || null }] : [],
                  })}
                >
                  {item.stock_name || item.ticker}
                </span>
                {!item.is_sold && (() => {
                  const useSell    = item.sell_id != null;
                  const hasAlert   = useSell
                    ? (item.sell_alert_up != null || item.sell_alert_down != null)
                    : (item.buy_alert_up  != null || item.buy_alert_down  != null);
                  const alertEnabled = useSell ? !!item.sell_alert_enabled : !!item.buy_alert_enabled;
                  const popupExtra   = useSell ? { sellId: item.sell_id } : { buyId: item.buy_id };
                  const typeNameUp   = useSell ? item.sell_alert_type_name_up   : item.buy_alert_type_name_up;
                  const typeNameDown = useSell ? item.sell_alert_type_name_down : item.buy_alert_type_name_down;
                  return (
                    <AlertButton
                      hasAlert={hasAlert}
                      alertEnabled={alertEnabled}
                      alertTypeNameUp={typeNameUp}
                      alertTypeNameDown={typeNameDown}
                      onClick={() => setAlertPopup({ ...popupExtra, ticker: item.ticker, stockName: item.stock_name, accountName: item.account_name, turnNo: item.turn_no })}
                    />
                  );
                })()}
              </>
            ) : col.id === 'diff_change' ? (
              !item.is_sold ? fDiff(item.prev_diff, item.diff_rate) : ''
            ) : col.id === 'turn_no' && activeTab === 'stock' ? (
              <span
                className="turn-no-compare-link"
                title="클릭 시 2개종목비교 그래프 보기"
                onClick={() => setCompareModal({
                  ticker:              item.ticker,
                  stockName:           item.stock_name,
                  compareType:         item.compare_type,
                  compareTicker:       item.compare_ticker,
                  compareIndexField:   item.compare_index_field,
                  defaultFrom:         item.buy_date,
                })}
              >
                {item.turn_no}차
              </span>
            ) : col.id === 'ticker' ? (
              <span
                onClick={() => handleTickerClick(item.ticker, item.market_type)}
                style={{
                  color:          item.ticker !== '현금잔액' ? '#1A73E8' : '#333333',
                  cursor:         item.ticker !== '현금잔액' ? 'pointer' : 'default',
                  textDecoration: item.ticker !== '현금잔액' ? 'underline' : 'none',
                }}
                title={item.ticker !== '현금잔액' ? '네이버 증권 차트 보기' : ''}
              >
                {item.ticker}
              </span>
            ) : col.id === 'sell_amount' ? (
              MEMO_TABS.has(activeTab) && item.sell_date ? (
                <span
                  className="sell-amount-cell-clickable"
                  title="클릭 시 매도등록 화면으로 이동"
                  onClick={() => goToSellRecord(item.sell_id)}
                >
                  {formatCell(col, item.sell_amount)}
                </span>
              ) : formatCell(col, item.sell_amount)
            ) : col.id === 'memo' ? (
              <span
                className="memo-cell memo-cell-clickable"
                title={item.memo ? `${item.memo}\n(클릭 시 매수등록 화면으로 이동)` : '클릭 시 매수등록 화면으로 이동'}
                onClick={() => goToBuyRecord(item.buy_id)}
              >
                {item.memo || ''}
              </span>
            ) : formatCell(col, item[col.id])}
          </td>
        );
      })}
    </tr>
  );

  // ── 소계/합계행 렌더 ───────────────────────────────────────
  const renderSummaryRow = (summary, label, type) => {
    const afterCols = columns.slice(LABEL_COLSPAN);
    return (
      <tr className={type === 'total' ? 'row-total' : 'row-subtotal'}>
        <td colSpan={LABEL_COLSPAN} className="summary-label">{label}</td>
        {afterCols.map(col => {
          const val = summary?.[col.id];
          return (
            <td key={col.id}
                style={{ textAlign: col.align, color: col.color ? profitColor(val) : undefined }}>
              {val != null ? formatCell(col, val) : ''}
            </td>
          );
        })}
      </tr>
    );
  };

  // ── 탭 내부 행 렌더 ────────────────────────────────────────
  const renderRow = (row, idx) => {
    switch (row.type) {
      case 'header':
        return (
          <tr key={`h-${idx}`} className="row-group-header">
            <td colSpan={columns.length}>
              {row.label}<span className="group-count"> ({row.count}건)</span>
            </td>
          </tr>
        );
      case 'data':     return renderDataRow(row.item, idx);
      case 'subtotal': return renderSummaryRow(row.summary, `${row.label} 소계`, 'subtotal');
      case 'total':    return renderSummaryRow(row.summary, `전체 합계 (${row.count}건)`, 'total');
      default: return null;
    }
  };

  // ── JSX ───────────────────────────────────────────────────
  return (
  <>
    <PageLayout title="건별수익률현황" toolbar={toolbar} message={message}>
      <div className="profit-body">

        {/* ── 지표 비교 (보유/매수와 무관한 순수 시세 워치리스트, 항상 펼침) ── */}
        {benchmark && (
          <div className="benchmark-bar">
            <div className="benchmark-panel">
              {buildBenchmarkChips(benchmark).map((chip, i) => (
                <React.Fragment key={chip.key}>
                  {i > 0 && <span className="benchmark-divider">|</span>}
                  {chip}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="profit-inner-tabs">
          {[
            { id: 'account', label: '계좌별' },
            { id: 'sector',  label: '섹터별' },
            { id: 'term',    label: '장단기별' },
            { id: 'stock',   label: '종목별' },
            { id: 'all',     label: '전체' },
          ].map(t => (
            <button
              key={t.id}
              className={`inner-tab-btn ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          {!autoRefresh && priceLoading  && <span className="price-status loading">시세 조회 중...</span>}
          {!autoRefresh && !priceLoading && priceSource === 'realtime' && <span className="price-status realtime">● 실시간 시세</span>}
          {!autoRefresh && !priceLoading && priceSource === 'mixed'    && <span className="price-status mixed">● 캐시 시세 포함</span>}

          {/* 자동갱신 — 탭 행 오른쪽 끝 */}
          <label className={`auto-refresh-chk ${autoRefresh ? 'active' : ''} ${autoRefresh && priceLoading ? 'fetching' : ''}`}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
            />
            {autoRefresh && priceLoading ? '시세조회중...' : '자동갱신(3분)'}
          </label>
          <button
            className={`profit-refresh-btn${(isRefreshing || priceLoading) ? ' spinning' : ''}`}
            onClick={handleManualRefresh}
            disabled={isRefreshing || priceLoading || !rawData.length}
            title="시세 수동 갱신"
          >
            ↻
          </button>
          <button
            className="profit-excel-btn"
            onClick={handleExcelDownload}
            disabled={!rawData.length}
            title="엑셀 다운로드"
          >
            📥 엑셀 다운로드
          </button>
        </div>

        <div className="profit-grid-wrapper">
          <table className="grid-table profit-grid">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.id}
                      style={{ width: col.w, minWidth: col.w, position: 'relative' }}
                      className={[
                        col.align === 'right' ? 'col-right' : col.align === 'center' ? 'col-center' : '',
                        col.sortable ? 'sortable-col' : '',
                      ].join(' ')}
                      onClick={col.sortable ? () => handleSort(col.id) : undefined}>
                    {col.label}
                    {col.id === 'current_price' && priceLoading && (
                      <span className="col-spinner"> ⟳</span>
                    )}
                    {col.sortable && sortArrow(col.id)}
                    {col.resizable && (
                      <span
                        className="col-resize-handle"
                        onMouseDown={e => startResize(e, col.id)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="grid-empty">
                    {loading ? '조회 중...' : '조회 버튼을 눌러 데이터를 불러오세요.'}
                  </td>
                </tr>
              ) : (
                renderRows.map((row, idx) => renderRow(row, idx))
              )}
            </tbody>
          </table>
        </div>

      </div>
    </PageLayout>
    {chartPopup && (
      <StockChartPopup
        ticker={chartPopup.ticker}
        stockName={chartPopup.stockName}
        accountName={chartPopup.accountName}
        termType={chartPopup.termType}
        currentPrice={chartPopup.currentPrice}
        diffRate={chartPopup.diffRate}
        turns={chartPopup.turns || []}
        onClose={() => setChartPopup(null)}
      />
    )}
    {alertPopup && (
      <AlertQuickPopup
        buyId={alertPopup.buyId}
        sellId={alertPopup.sellId}
        ticker={alertPopup.ticker}
        stockName={alertPopup.stockName}
        accountName={alertPopup.accountName}
        turnNo={alertPopup.turnNo}
        onClose={() => setAlertPopup(null)}
        onSaved={() => { setAlertPopup(null); fetchData(false); }}
      />
    )}
    {compareModal && (
      <StockCompareModal
        ticker={compareModal.ticker}
        stockName={compareModal.stockName}
        compareType={compareModal.compareType}
        compareTicker={compareModal.compareTicker}
        compareIndexField={compareModal.compareIndexField}
        defaultFrom={compareModal.defaultFrom}
        onClose={() => setCompareModal(null)}
      />
    )}
  </>
  );
};

export default ProfitStatusPage;
